import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { getEmpresa, getPlanes, cambiarEstadoEmpresa, cambiarPlanEmpresa, regenerarPassword, EmpresaDetalle, PlanDto } from '../../services/adminService';
import './EmpresaDetailPage.css';

const EmpresaDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState<EmpresaDetalle | null>(null);
  const [planes, setPlanes] = useState<PlanDto[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nuevoPlan, setNuevoPlan] = useState('');
  const [guardandoPlan, setGuardandoPlan] = useState(false);
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [passwordTemporal, setPasswordTemporal] = useState<string | null>(null);
  const [regenerando, setRegenerando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const cargar = useCallback(async () => {
    if (!id) return;
    setCargando(true);
    setError(null);
    try {
      const [emp, pl] = await Promise.all([getEmpresa(Number(id)), getPlanes()]);
      setEmpresa(emp);
      setPlanes(pl);
      setNuevoPlan(emp.plan?.codigo ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresa');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCambiarPlan = async () => {
    if (!empresa || nuevoPlan === empresa.plan?.codigo) return;
    setGuardandoPlan(true);
    try {
      await cambiarPlanEmpresa(empresa.id, nuevoPlan);
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cambiar plan');
    } finally {
      setGuardandoPlan(false);
    }
  };

  const handleCambiarEstado = async () => {
    if (!empresa) return;
    setGuardandoEstado(true);
    try {
      await cambiarEstadoEmpresa(empresa.id, !empresa.estaActiva);
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cambiar estado');
    } finally {
      setGuardandoEstado(false);
    }
  };

  const handleRegenerarPassword = async () => {
    if (!empresa) return;
    setRegenerando(true);
    try {
      const pwd = await regenerarPassword(empresa.id);
      setPasswordTemporal(pwd);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al regenerar contraseña');
    } finally {
      setRegenerando(false);
    }
  };

  const copiar = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  if (cargando) return <AdminLayout><div className="det-loading"><i className="fas fa-spinner fa-spin"></i> Cargando...</div></AdminLayout>;
  if (error) return <AdminLayout><div className="det-error"><i className="fas fa-exclamation-circle"></i> {error}</div></AdminLayout>;
  if (!empresa) return null;

  return (
    <AdminLayout>
      <div className="det-page">
        <div className="det-header">
          <button className="det-back" onClick={() => navigate('/admin/empresas')}>
            <i className="fas fa-arrow-left"></i> Volver
          </button>
          <div className="det-title-row">
            <h1 className="det-title">{empresa.nombre}</h1>
            <span className={`det-estado ${empresa.estaActiva ? 'activa' : 'inactiva'}`}>
              {empresa.estaActiva ? 'Activa' : 'Inactiva'}
            </span>
          </div>
          <p className="det-sub">Admin: {empresa.emailAdmin || '—'} · Alta: {new Date(empresa.fechaAlta).toLocaleDateString('es-AR')}</p>
        </div>

        <div className="det-grid">
          {/* Stats */}
          <div className="det-card">
            <h3 className="det-card-title">Resumen</h3>
            <div className="det-stats">
              <div className="det-stat">
                <span className="det-stat-value">{empresa.cantEmpleados}</span>
                <span className="det-stat-label">Empleados activos</span>
              </div>
              <div className="det-stat">
                <span className="det-stat-value">{empresa.plan?.maxEmpleados ?? '?'}</span>
                <span className="det-stat-label">Límite del plan</span>
              </div>
              <div className="det-stat">
                <span className="det-stat-value">{empresa.cantUsuarios}</span>
                <span className="det-stat-label">Usuarios activos</span>
              </div>
            </div>
          </div>

          {/* Plan */}
          <div className="det-card">
            <h3 className="det-card-title">Plan actual</h3>
            <div className="det-plan-info">
              <span className="det-plan-badge">{empresa.plan?.nombre ?? '—'}</span>
              <span className="det-plan-price">${empresa.plan?.precioUsdMes ?? 0}/mes</span>
            </div>
            <div className="det-plan-change">
              <select value={nuevoPlan} onChange={e => setNuevoPlan(e.target.value)} className="det-select">
                {planes.filter(p => p.activo).map(p => (
                  <option key={p.codigo} value={p.codigo}>{p.nombre} — ${p.precioUsdMes}/mes ({p.maxEmpleados} emp.)</option>
                ))}
              </select>
              <button
                className="det-btn-primary"
                onClick={handleCambiarPlan}
                disabled={guardandoPlan || nuevoPlan === empresa.plan?.codigo}
              >
                {guardandoPlan ? <><div className="det-spinner"></div> Guardando</> : 'Cambiar plan'}
              </button>
            </div>
          </div>

          {/* Estado */}
          <div className="det-card">
            <h3 className="det-card-title">Estado de la cuenta</h3>
            <p className="det-estado-desc">
              {empresa.estaActiva
                ? 'La empresa tiene acceso activo al sistema.'
                : 'La empresa está suspendida y no puede acceder al sistema.'}
            </p>
            <button
              className={`det-btn-estado ${empresa.estaActiva ? 'suspender' : 'activar'}`}
              onClick={handleCambiarEstado}
              disabled={guardandoEstado}
            >
              {guardandoEstado ? (
                <><div className="det-spinner"></div> Procesando</>
              ) : empresa.estaActiva ? (
                <><i className="fas fa-ban"></i> Suspender empresa</>
              ) : (
                <><i className="fas fa-check-circle"></i> Activar empresa</>
              )}
            </button>
          </div>

          {/* Acceso admin */}
          <div className="det-card">
            <h3 className="det-card-title">Acceso del administrador</h3>
            <p className="det-pwd-desc">
              Genera una nueva contraseña temporal para el admin de esta empresa.
            </p>
            {passwordTemporal ? (
              <div className="det-password-box">
                <span className="det-password-label">Nueva contraseña:</span>
                <div className="det-password-row">
                  <code>{passwordTemporal}</code>
                  <button onClick={() => copiar(passwordTemporal)} className="det-copy-btn" title="Copiar">
                    <i className={`fas ${copiado ? 'fa-check' : 'fa-copy'}`}></i>
                  </button>
                </div>
                <p className="det-password-warning">
                  <i className="fas fa-exclamation-triangle"></i> Guardala ahora, no se mostrará de nuevo.
                </p>
              </div>
            ) : (
              <button className="det-btn-secondary" onClick={handleRegenerarPassword} disabled={regenerando}>
                {regenerando ? <><div className="det-spinner"></div> Generando</> : <><i className="fas fa-key"></i> Regenerar contraseña</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmpresaDetailPage;
